import { CommunityId, emailTokens } from "@sp-crm/core";
import { MandatoryEmailToken } from "components/messages/mandatory-token";
import { MessageComposeInline } from "components/messages/message-compose-internal";
import { PlacementMessageCompose } from "components/messages/types";
import { fancyAlert } from "components/ui/fancy-alert";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    AdvancedSearchEntityType,
    ObservationKind,
    useRequestCommunityListingParticipationMutation,
} from "generated/graphql";
import React, { MutableRefObject, useCallback } from "react";
import { useQueryClient } from "react-query";
import { useCanRequestCommunityAvailability } from "store/selectors/hooks";
import { handleEmailSent } from "util/email-sent";

interface Props {
    communityId: CommunityId;
    onDismiss: () => void;
    onSuccess: () => void;
    observationFocus?: ObservationKind;
}

export const CommunityRequestListingSignupPanel: React.FC<Props> = props => {
    const canRequestAvailability = useCanRequestCommunityAvailability();
    const {
        communityId,
        onDismiss,
        onSuccess,
        observationFocus = ObservationKind.Availability,
    } = props;
    const queryClient = useQueryClient();
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const inlineEditorRef =
        React.useRef<PlacementMessageCompose>() as MutableRefObject<PlacementMessageCompose>;
    const requestCommunityListingParticipation =
        useRequestCommunityListingParticipationMutation();

    const handleSend = useCallback(async () => {
        const message = inlineEditorRef.current.getMessage();
        try {
            if (!(message.body || "").includes(emailTokens.liveLink)) {
                const errorMessage = `To send the form the message body must contain ${emailTokens.liveLink}. To proceed, add ${emailTokens.liveLink} to the body and send again.`;
                setErrorMessage(errorMessage);
                await fancyAlert("Link Placeholder Missing", errorMessage);
                return;
            }
            setErrorMessage(null);

            await requestCommunityListingParticipation.mutateAsync({
                communityId,
                outboundMessage: message,
                observationFocus,
            });
            handleEmailSent(queryClient);
            onSuccess();
        } catch (e) {
            setErrorMessage(e.message);
        }
    }, [
        communityId,
        inlineEditorRef,
        requestCommunityListingParticipation,
        onSuccess,
        observationFocus,
        queryClient,
    ]);

    if (!canRequestAvailability) {
        return null;
    }

    const observationConfig: Record<
        ObservationKind,
        {
            headerText: string;
            description: string;
            specialExperience: "request-pricing" | "request-availability";
            initialSubject: string;
            initialBody: string;
        }
    > = {
        [ObservationKind.Pricing]: {
            headerText: "Request community pricing",
            description:
                "Sends a message with a link the community can use to share their pricing information.",
            specialExperience: "request-pricing",
            initialSubject: "Current pricing information?",
            initialBody:
                "Hi!<br><br>When you have a moment, could you share your current pricing information below? It would really help me understand what options are available. Thanks so much!<br><br>{{link}}",
        },
        [ObservationKind.Availability]: {
            headerText: "Request community availability",
            description:
                "Sends a message with a link the community can use to share their availability.",
            specialExperience: "request-availability",
            initialSubject: "Any openings right now?",
            initialBody:
                "Hi!<br><br>When you have a moment, could you update your availability below? It's really quick, and will help me know as soon as you have vacancies. Thanks so much!<br><br>{{link}}",
        },
    };

    const { headerText, description, specialExperience, initialSubject, initialBody } =
        observationConfig[observationFocus];

    return (
        <ResponsivePanel
            type={PanelType.extraLarge}
            onDismiss={onDismiss}
            isOpen={true}
            headerText={headerText}>
            <div className="space-y-4">
                <p>{description}</p>
                <MessageComposeInline
                    entityId={communityId}
                    entityType={AdvancedSearchEntityType.Community}
                    onDismiss={onDismiss}
                    ref={inlineEditorRef}
                    requestSpecialMessageExperience={specialExperience}
                    initialBody={initialBody}
                    initialSubject={initialSubject}
                />
                <div className="my-1">
                    <MandatoryEmailToken use={specialExperience} />
                </div>
                <div className="flex items-center justify-end space-x-2">
                    <SecondaryButton
                        type="button"
                        onClick={onDismiss}
                        disabled={requestCommunityListingParticipation.isLoading}>
                        Discard
                    </SecondaryButton>
                    <PrimaryButton
                        type="button"
                        onClick={handleSend}
                        disabled={requestCommunityListingParticipation.isLoading}>
                        {requestCommunityListingParticipation.isLoading
                            ? "Sending..."
                            : "Send"}
                    </PrimaryButton>
                </div>
                {errorMessage ? (
                    <InlineBanner type="error">{errorMessage}</InlineBanner>
                ) : null}
            </div>
        </ResponsivePanel>
    );
};
